import { useEffect, useState } from "react";
import axios from "axios";
import jwt from "jwt-decode";
import { GetUserByEmail_getUserByEmail } from "../__generated__/gql-types/GetUserByEmail";
import { notification } from "antd";
import { environment } from "../configuration/environment";
import { useDispatch, useSelector } from "react-redux";
import { TOKEN, USER_AUTH } from "../store/selectors";
import { useApolloClientContext } from "../components/Provider/ApolloClientContext";
import { useMonitoring } from "./useMonitoring";

interface UseAuthType {
  user: GetUserByEmail_getUserByEmail | null;
  loading: boolean;
  error?: any;
  isAuthenticated: boolean;
  token: string;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = (): UseAuthType => {
  const dispatch = useDispatch();
  const { level } = useApolloClientContext();
  const user = useSelector(USER_AUTH);
  const token = useSelector(TOKEN);
  const monitor = useMonitoring();
  const [error, setError] = useState<any>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchInfo = async () => {
    setLoading(true);
    let localToken = localStorage.getItem("token");
    try {
      if (!localToken) {
        setIsAuthenticated(false);
        clearAuthStorage();
      } else {
        setError(null);
        setIsAuthenticated(true);
        const foundUser: GetUserByEmail_getUserByEmail | null =
          await getUserWithToken(localToken);
        foundUser &&
          dispatch({
            type: "auth/setAuth",
            payload: {
              token: localToken,
              user: foundUser,
            },
          });
      }
    } catch (error) {
      setIsAuthenticated(false);
      clearAuthStorage();
    } finally {
      setLoading(false);
    }
  };

  const getUserWithToken = async (
    token: string
  ): Promise<GetUserByEmail_getUserByEmail | null> => {
    if (!token) return null;
    const user: GetUserByEmail_getUserByEmail = await jwt(token);

    return user;
  };

  const login = async (emailOrUsername: string, password: string) => {
    setLoading(true);
    try {
      // Use the correct backend URL for login based on the current level
      const backendUrl =
        level === "secundaria"
          ? environment.secundariaGraphQLURL.replace(/\/graphql$/, "")
          : environment.primariaGraphQLURL.replace(/\/graphql$/, "");
      monitor.captureMessage("Login", "info", { email: emailOrUsername, level });
      const { data } = await axios.post(`${backendUrl}/login`, {
        emailOrUsername,
        password,
      });

      localStorage.setItem("token", data.token);
      setIsAuthenticated(true);
      const foundUser: GetUserByEmail_getUserByEmail | null =
        await getUserWithToken(data.token);
      if (foundUser) {
        monitor.setUser({ 
          id: String((foundUser as GetUserByEmail_getUserByEmail).id ?? ""), 
          email: emailOrUsername, 
          username: `${foundUser?.first_name} ${foundUser?.last_name}`
        });
        dispatch({
          type: "auth/setAuth",
          payload: { token: data.token, user: foundUser },
        });
      }
    } catch (error) {
      setIsAuthenticated(false);
      notification["error"]({
        message: "Error",
        description: "Usuario o contraseña incorrectos!!",
        placement: "top",
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const clearAuthStorage = () => {
    localStorage.setItem("token", "");
    dispatch({ type: "auth/clearAuth" });
    monitor.clearUser();
  };

  const logout = async () => {
    setLoading(true);
    try {
      clearAuthStorage();
      setIsAuthenticated(false);
    } catch (error) {
      setIsAuthenticated(false);
      notification["error"]({
        message: "Error",
        description: "Error al desloguearse, por favor reintente!",
        placement: "top",
        duration: 3,
      });
    } finally {
      setError("");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return {
    user,
    loading,
    error,
    isAuthenticated,
    token,
    login,
    logout,
  };
};
